/**
 * Redeeming an invitation link. The token is checked server-side (POST /api/invites/accept),
 * which also creates the membership - the browser has no rights to do that itself.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAcceptInvite } from "@/hooks/use-workspaces";
import { useAuth } from "@/lib/session/auth-context";
import { useWorkspace } from "@/lib/session/workspace-context";

export function InvitePage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const { isAuthenticated, loading } = useAuth();
  const { select } = useWorkspace();
  const navigate = useNavigate();
  const acceptInvite = useAcceptInvite();
  const [error, setError] = useState("");
  const attempted = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !token || attempted.current) return;
    attempted.current = true;
    acceptInvite
      .mutateAsync(token)
      .then((result) => {
        select(result.workspace);
        navigate("/dashboard", { replace: true });
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : t("invite.failed"));
      });
  }, [isAuthenticated, token, acceptInvite, select, navigate, t]);

  if (loading) return null;
  // Sign in first, then come back to this exact link.
  if (!isAuthenticated)
    return <Navigate to="/login" replace state={{ from: `/invite/${token}` }} />;

  return (
    <div className="mx-auto max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>{t("invite.title")}</CardTitle>
          <CardDescription>{error ? t("invite.problem") : t("invite.working")}</CardDescription>
        </CardHeader>
        {error && (
          <CardContent className="space-y-4">
            <p role="alert" className="text-sm text-destructive" data-testid="invite-error">
              {error}
            </p>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              {t("invite.toDashboard")}
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
